import {
  Box,
  VStack,
  Heading,
  Text,
  HStack,
  Icon,
  Button,
  Accordion,
  SimpleGrid,
  Image,
} from "@chakra-ui/react";
import {
  BiQuestionMark,
  BiBug,
  BiEnvelope,
  BiMessageRoundedDetail,
  BiCheckCircle,
  BiShield,
} from "react-icons/bi";
import useThemeColors from "../../hooks/useThemeColors";
import { useStyles } from "../../hooks/useStyles";
import Cad from "../CustomUi/Cad";
import useIcons from "../../hooks/useIcons";
import { GOOGLE_BUG_FORM_URL } from "../../services/constants";
import { GOOGLE_FEATURE_FORM_URL } from "../../services/constants";
import { CALENDLY_URL } from "../../services/constants";

function Help() {
  const colors = useThemeColors();
  const icons = useIcons();
  const { scrollBar } = useStyles();



  const faqs = [
    {
      question: "How do I add a new student?",
      answer:
        "Go to Students > 'Actions' > 'Add Student'. Fill in student details and save.",
    },
    {
      question: "Can I change the school logo?",
      answer:
        "Absolutely. You can change the school logo as well as the school name and other settings in the 'Admin' > 'School Settings' section.",
    },
    {
      question: "Is my data safe?",
      answer:
        "Yes. Your data is safe and secure. We use the latest encryption technology to ensure the system is GDPR compliant and to protect and encrypt sensitive student information both in transit and at rest.",
    },
    {
      question: "Can I bulk import students or guardians?",
      answer:
        "Yes. In Students, use the Import option in the 'Actions' menu to upload a CSV using the provided template. The system validates entries and shows feedback after saving.",
    },
    {
      question:
        "How do I set up sessions, terms, class levels or categories, subjects, grade policies and assessment types?",
      answer:
        "Use the 'Admin' menu to customise academic settings. The app is designed to be flexible and allow you to set up your school's academic structure as you see fit. You can create multiple sessions, terms, levels, classes, subjects, grade policies and assessment types as needed. The admin section is only accessible to a user with the Admin role.",
    },
    {
      question: "How do I enter results and generate report cards?",
      answer:
        "Open Results from the navigation menu, choose a class, then a student, and you can enter or modify scores. Once you have finished entering scores, use 'Preview' to see the report card, and then use 'Ctrl + P' to print.",
    },
    {
      question: "How are grades calculated?",
      answer:
        "Grades follow the configuration in 'Admin' > Grade Criteria Settings (ranges, grade letters, remarks). Update criteria to match your policy.",
    },
    {
      question: "How does student promotion work?",
      answer:
        "Go to Promotion. Use Eligibility to preview who qualifies based on rules that you set up in the 'Admin' > Levels settings, adjust Promotion Rules if needed, then run the promotion and review History.",
    },
    {
      question: "How do I customize school branding (colors and texts)?",
      answer:
        "'Admin' > 'School Settings' lets you update accent colors and key school information used across the app and on printed reports.",
    },
    {
      question: "Can I add other users to the system?",
      answer:
        "Yes. Use 'Admin' > 'User Settings' to add new users, edit profiles, and assign roles. Access to admin pages is restricted to authorized roles.",
    },
    {
      question: "How do I manage users and roles?",
      answer:
        "'Admin' > User Settings. Add new users, edit profiles, and assign roles. Access to admin pages is restricted to authorized roles.",
    },

    {
      question: "Can I export or back up data?",
      answer:
        "Yes. Use Admin > Data Management > Export Data to download CSV/Excel for entities like students, classes, and results.",
    },
    {
      question: "Is there an audit trail?",
      answer:
        "Yes. Audit Trail lets you search and filter activity logs (who did what and when) for accountability.",
    },

    {
      question: "How do I reset my password or activate my account?",
      answer:
        "Use 'Forgot Password' on the login page to reset. Activation links are sent via email for new accounts.",
    },
    {
      question: "How can I book a demo or get help?",
      answer:
        "From this Help page, use the 'Book a Demo' button to schedule on Calendly, or contact support via email.",
    },
  ];

  return (
    <Box
      flex={1}
      h="100%"
      bg={colors.background.primary}
      p={4}
      overflowY="auto"
      css={scrollBar}
    >
      <VStack gap={8} align="stretch" mx="auto" maxW="1200px">
        {/* Page Header */}
        <Box textAlign="center" py={8}>
          <Image
            src={colors.logo}
            alt="Result logo"
            height={{ base: 10, md: 12 }}
            mx="auto"
            mb={4}
            draggable={false}
          />
          <Heading size="2xl" color={colors.text.primary} mb={4}>
            Help & Support Center
          </Heading>
          <Text
            color={colors.text.secondary}
            fontSize="lg"
            maxW="600px"
            mx="auto"
          >
            Get help with using Result, report issues, and find answers to
            common questions
          </Text>
        </Box>

        {/* Support & Contact Options */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
          {/* Report Issues */}
          <Cad>
            <VStack gap={4} align="stretch">
              <HStack gap={3} align="center">
                <Icon as={BiBug} boxSize={6} color="red.500" />
                <Heading size="md" color={colors.text.primary}>
                  Report Issues
                </Heading>
              </HStack>
              <Text color={colors.text.secondary} fontSize="sm">
                Found a bug or experiencing technical issues? Submit via Google
                Forms.
              </Text>
              <VStack gap={2} align="start">
                <HStack gap={2}>
                  <Icon as={BiCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Include screenshots if possible
                  </Text>
                </HStack>
                <HStack gap={2}>
                  <Icon as={BiCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Describe the steps to reproduce
                  </Text>
                </HStack>
                <HStack gap={2}>
                  <Icon as={BiCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Mention your browser and device
                  </Text>
                </HStack>
              </VStack>
              <Button
                colorPalette="red"
                variant="surface"
                size="sm"
                onClick={() => window.open(GOOGLE_BUG_FORM_URL, "_blank")}
              >
                <Icon as={BiBug} mr={2} />
                Report Bug
              </Button>
            </VStack>
          </Cad>

          {/* Feature Suggestions */}
          <Cad>
            <VStack gap={4} align="stretch">
              <HStack gap={3} align="center">
                <Icon
                  as={BiMessageRoundedDetail}
                  boxSize={6}
                  color="brand.500"
                />
                <Heading size="md" color={colors.text.primary}>
                  Feature Suggestions
                </Heading>
              </HStack>
              <Text color={colors.text.secondary} fontSize="sm">
                Tell us what you’d like to see next.
              </Text>
              <Button
                colorPalette="teal"
                variant="surface"
                size="sm"
                onClick={() => window.open(GOOGLE_FEATURE_FORM_URL, "_blank")}
              >
                Share a Suggestion
              </Button>
            </VStack>
          </Cad>

          {/* Contact Support */}
          <Cad>
            <VStack gap={4} align="stretch">
              <HStack gap={3} align="center">
                <Icon
                  as={BiMessageRoundedDetail}
                  boxSize={6}
                  color="blue.500"
                />
                <Heading size="md" color={colors.text.primary}>
                  Contact Support
                </Heading>
              </HStack>
              <Text color={colors.text.secondary} fontSize="sm">
                Need personalized help? Our support team is here to assist you.
              </Text>
              <VStack gap={3} align="stretch">
                <HStack gap={3}>
                  <Icon as={BiEnvelope} color="blue.500" />
                  <Text fontSize="sm" color={colors.text.secondary}>
                    support@resultsbynumero
                  </Text>
                </HStack>
              </VStack>
              <Button
                colorPalette="blue"
                variant="surface"
                size="sm"
                onClick={() =>
                  window.open("mailto:support@resultsbynumero", "_blank")
                }
              >
                <Icon as={BiEnvelope} mr={2} />
                Send Email
              </Button>
            </VStack>
          </Cad>
        </SimpleGrid>

        {/* FAQ Section */}
        <Cad>
          <VStack gap={6} align="stretch">
            <HStack gap={4} align="center">
              <Icon as={BiQuestionMark} boxSize={6} color={"brand.500"} />
              <Heading size="md">Frequently Asked Questions</Heading>
            </HStack>
            <Accordion.Root collapsible>
              {faqs.map((faq, index) => (
                <Accordion.Item key={index} value={`faq-${index}`}>
                  <Accordion.ItemTrigger>
                    <Text flex="1" textAlign="left" fontWeight="medium">
                      {faq.question}
                    </Text>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent>
                    <Accordion.ItemBody pb={4} color={colors.text.secondary}>
                      {faq.answer}
                    </Accordion.ItemBody>
                  </Accordion.ItemContent>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </VStack>
        </Cad>

        {/* Feedback & Booking */}
        <Cad>
          <VStack gap={6} align="stretch">
            <HStack gap={4} align="center">
              <Icon
                as={icons.utility.chalkboard}
                boxSize={6}
                color={"brand.500"}
              />
              <Heading size="md">Book a Demo / Consultation</Heading>
            </HStack>
            <VStack gap={2} align="start">
              <Text color={colors.text.secondary} fontSize="sm">
                Schedule a live walkthrough or discuss your school’s workflow.
              </Text>
              <Button
                onClick={() => window.open(CALENDLY_URL, "_blank")}
                colorPalette="teal"
                variant="surface"
                size="sm"
                alignSelf="start"
              >
                Book on Calendly
              </Button>
            </VStack>
          </VStack>
        </Cad>

        {/* Demo Video
        <Cad>
          <VStack gap={4} align="stretch">
            <HStack gap={3} align="center">
              <Icon
                as={BiPlay}
                boxSize={6}
                color={colors.schoolAccent.icon}
              />
              <Heading size="md" color={colors.text.primary}>
                Watch a Quick Demo
              </Heading>
            </HStack>
            <Text color={colors.text.secondary} fontSize="sm">
              A short overview of Result features and how to get started.
            </Text>
            <Box
              borderWidth={1}
              borderColor={colors.border.primary}
              borderRadius="md"
              overflow="hidden"
              bg={colors.background.secondary}
            >
              <AspectRatio ratio={16 / 9}>
                <iframe
                  src={YOUTUBE_DEMO_EMBED_URL}
                  title="Result Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
            </Box>
          </VStack>
        </Cad> */}

        {/* System Information */}
        <Cad>
          <VStack gap={4} align="stretch">
            <HStack gap={3} align="center">
              <Icon as={BiShield} boxSize={6} color="green.500" />
              <Heading size="md" color={colors.text.primary}>
                System Information
              </Heading>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={colors.text.primary}
                >
                  Version
                </Text>
                <Text fontSize="sm" color={colors.text.secondary}>
                  Result v1.0
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={colors.text.primary}
                >
                  Made and maintained with ❤️
                </Text>
                <Text fontSize="sm" color={colors.text.secondary}>
                  © {new Date().getFullYear()} Result. All rights
                  reserved.
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </Cad>
      </VStack>
    </Box>
  );
}

export default Help;
